'use client'

import Text from '@/components/custom-ui/text'
import ShoppingBag from '@/components/icons/shopping-bag'
import { Button } from '@/components/ui/button'

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  useCartCount,
  useCartSidebar,
  useCartStore,
  withCartStoreSuspense,
} from '@/stores/cart-store'
import { X } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import Link from 'next/link'
import CartItem from '@/components/custom-ui/cart/cart-item'
import EmptyState from './empty-state'
import { useDictionary } from './shopping-cart.dictionary'
import apply from '@/localization/apply'
import OfferCard from './offer-card'
import { EligibilitySectionData } from '@/lib/api/strapi/eligibility-section/eligibility-section.types'
import SaveCartDialog from '@/components/composites/saved-carts/save-cart-dialog'
import { Dictionary } from '@/localization/dictionary'
import { getOfferType, getRemainingPoints } from '@/lib/api/shopware/products/cutom_fields.utils'
import { CartContent } from '@/lib/api/shopware/cart/cart.types'

type Props = {
  eligibilitySection?: EligibilitySectionData
  salesChannel: string
  saveCartDict: Dictionary['savedCarts']['save']
  isAuthenticated: boolean
}

const ShoppingCartSidebar = ({
  eligibilitySection,
  salesChannel,
  saveCartDict,
  isAuthenticated,
}: Props) => {
  const dict = useDictionary()
  const cart = useCartStore()
  const { showCart, toggleShowCart } = useCartSidebar()

  const isB2b = salesChannel.includes('B2B')

  const count = useCartCount()
  const hostOfferPoints = cart?.extensions?.customMessagesHostOffer?.data
  const customerOfferPoints =
    cart?.extensions?.customMessagesCustomerOffer?.data
  const freeProductsPoints = cart?.extensions?.customMessagesFreeProducts?.data

  const pointsFormatted = {
    hostOffer: Math.max(0, hostOfferPoints?.customerCanSelectQuantity ?? 0),
    customerOffer: Math.max(0, customerOfferPoints?.customerCanSelectQuantity ?? 0),
    freeProducts: Math.max(0, freeProductsPoints?.remainingPoints ?? 0),
  }

  const remainingPoints = {
    host: getRemainingPoints(cart?.extensions, 'host'),
    customer: getRemainingPoints(cart?.extensions, 'customer'),
    free: getRemainingPoints(cart?.extensions, 'free')
  }

  const getMaxqty = (item: CartContent['lineItems'][0]): number | undefined => {
    const offerType = getOfferType(item, salesChannel)
    if (!offerType) {
      return undefined
    }

    const points = remainingPoints[offerType]
    return Math.floor(item.quantity + points)
  }

  return (
    <Sheet open={showCart} onOpenChange={toggleShowCart}>
      <SheetContent aria-describedby={undefined} className="flex h-full w-full max-w-[400px] flex-1 flex-col px-5 py-5 sm:px-10">
        <SheetTitle className="sr-only">{apply(dict.itemCount, { count })}</SheetTitle>
        <SheetHeader className="mb-9 flex-row gap-x-2 space-y-0 border-b-[0.4px] border-secondary-200 py-2">
          <div className="flex flex-1 items-center">
            <ShoppingBag className="mr-1 size-6" />
            <Text as="p" className="flex items-center">
              {apply(dict.itemCount, { count })}
            </Text>
          </div>
          <SheetClose asChild>
            <Button size="icon" className="size-6" variant={'secondary'}>
              <X />
            </Button>
          </SheetClose>
        </SheetHeader>
        {count === 0 ? (
          <EmptyState dict={dict.emptyState} />
        ) : (
          <>
            <ScrollArea className="my-4">
              <div className="flex flex-1 flex-col gap-1">
                {cart?.lineItems.map((item) => {
                  const dynamicMax = getMaxqty(item)
                  return <CartItem
                    data={item}
                    salesChannel={salesChannel}
                    key={item.id}
                    max={dynamicMax}
                    onClick={toggleShowCart}
                    eligibilitySection={eligibilitySection?.attributes}
                    dict={dict}
                  />
                })}
              </div>

              <div className="mt-4 flex flex-col gap-2">
                {isB2b ? (
                  <>
                    <OfferCard
                      points={pointsFormatted.hostOffer}
                      eligibilitySection={
                        eligibilitySection?.attributes.host_offers
                      }
                      label={pointsFormatted.hostOffer > 1 ? dict.hostOffer.labelPlural: dict.hostOffer.label}
                      pointsText={dict.hostOffer.pointPlural}
                      dict={dict}
                    />
                    <OfferCard
                      points={pointsFormatted.customerOffer}
                      eligibilitySection={
                        eligibilitySection?.attributes.customers_offers
                      }
                      label={pointsFormatted.customerOffer > 1 ? dict.customerOffer.customerOffers: dict.customerOffer.customerOffer}
                      pointsText={dict.customerOffer.point}
                      dict={dict}
                    />
                    <OfferCard
                      points={pointsFormatted.freeProducts}
                      eligibilitySection={
                        eligibilitySection?.attributes.free_offers
                      }
                      label={pointsFormatted.freeProducts > 1 ? dict.freeProducts.labelPlural: dict.freeProducts.label}
                      iconSrc="/icons/gift.svg"
                      pointsText={dict.hostOffer.point}
                      dict={dict}
                    />
                  </>
                ) : (
                  <OfferCard
                    points={pointsFormatted.customerOffer}
                    eligibilitySection={
                      eligibilitySection?.attributes.customers_offers
                    }
                    label={pointsFormatted.customerOffer > 1 ? dict.customerOffer.customerOffers: dict.customerOffer.customerOffer}
                    pointsText={dict.hostOffer.pointPlural}
                    dict={dict}
                  />
                )}
              </div>
            </ScrollArea>
            <SheetFooter>
              <div className="flex w-full flex-col gap-2">
                {isAuthenticated && isB2b && <SaveCartDialog dict={saveCartDict} />}
                <SheetClose asChild>
                  <Button asChild className="w-full uppercase">
                    <Link href="/shopping-bag">
                      {dict.viewBag}
                    </Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default withCartStoreSuspense(ShoppingCartSidebar)